import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import DOMPurify from 'dompurify';
import { Helmet } from 'react-helmet-async';
import { articleService, Article } from '../services/api';
import { reportViewWhenRead } from '../lib/articleView';
import { clampDescription } from '../lib/metaDescription';
import { articleShareImage } from '../lib/shareImage';
import ArticleCard from '../components/ArticleCard';
import ReadingProgress from '../components/ReadingProgress';
import { ArticlePageSkeleton } from '../components/Skeleton';
import { calculateReadingTime, generateTableOfContents, addHeadingIds } from '../utils/articleUtils';

import RelatedGuides from '../components/RelatedGuides';
import MobileArticleAd from '../components/MobileArticleAd';
import ChartPrepCalculator from '../components/ChartPrepCalculator';
import ChatAbbrevLookup from '../components/ChatAbbrevLookup';
import BraumsLunchStatus from '../components/BraumsLunchStatus';
import Comments from '../components/Comments';
import AdUnit from '../components/AdUnit';
import '../styles/article.css';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

// Normalize SSR article data: flatten nested author/category objects to match Article interface
const normalizeSSRArticle = (data: any): Article | null => {
  if (!data) return null;
  // If already normalized (author_name is a string), return as-is
  if (typeof data.author_name === 'string' && typeof data.category === 'string') return data as Article;

  const author = data.author;
  const category = data.category;
  const authorFullName = (author && typeof author === 'object')
    ? ((author.first_name || author.last_name)
        ? `${author.first_name || ''} ${author.last_name || ''}`.trim()
        : author.username || 'Anonymous')
    : (data.author_name || 'Anonymous');

  return {
    ...data,
    author: (author && typeof author === 'object') ? author.id : author,
    author_name: authorFullName,
    author_username: (author && typeof author === 'object') ? author.username : data.author_username,
    category: (category && typeof category === 'object') ? category.slug : category,
    category_name: (category && typeof category === 'object') ? category.name : (data.category_name || ''),
    author_bio: (author && typeof author === 'object') ? author.bio : data.author_bio,
    author_avatar: (author && typeof author === 'object') ? author.profile_image : data.author_avatar,
    author_article_count: (author && typeof author === 'object') ? author.articles_count : data.author_article_count,
    author_twitter: (author && typeof author === 'object') ? author.twitter_handle : data.author_twitter,
    author_linkedin: (author && typeof author === 'object') ? author.linkedin_url : data.author_linkedin,
  } as Article;
};

const WIDGET_REGISTRY: Record<string, React.FC> = {
  'chart-calc': ChartPrepCalculator,
  'chat-abbrev': ChatAbbrevLookup,
  'braums-lunch': BraumsLunchStatus,
};

const EnhancedArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  // Check for SSR-provided article data to avoid loading skeleton CLS
  const ssrData = typeof window !== 'undefined' ? normalizeSSRArticle((window as any).__ARTICLE_DATA__) : null;
  const [article, setArticle] = useState<Article | null>(ssrData || null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(!ssrData);
  const [error, setError] = useState<string | null>(null);
  const [showToc, setShowToc] = useState(false);

  const { readingTime, toc, sanitizedContent } = useMemo(() => {
    if (!article?.content) {
      return { readingTime: 0, toc: [], sanitizedContent: '' };
    }
    const cleanContent = DOMPurify.sanitize(article.content, {
      ADD_TAGS: ['img', 'iframe'],
      ADD_ATTR: ['id', 'src', 'alt', 'width', 'height', 'style', 'class', 'frameborder', 'allow', 'allowfullscreen', 'loading', 'itemscope', 'itemprop', 'itemtype'],
    });
    const contentWithIds = addHeadingIds(cleanContent);
    return {
      readingTime: calculateReadingTime(article.content),
      toc: generateTableOfContents(cleanContent),
      sanitizedContent: contentWithIds
    };
  }, [article?.content]);

  // Auto-detect FAQ sections and generate FAQPage JSON-LD schema
  const faqSchema = useMemo(() => {
    if (!article?.content) return null;
    const content = article.content;
    // Scope to the FAQ SECTION ONLY. Running to the end of the article scraped later
    // section headings as questions and produced invalid FAQPage markup.
    const faqHeading = content.search(/<h[23][^>]*>[^<]*(?:Frequently Asked Questions|FAQs?\b)/i);
    if (faqHeading === -1) return null;
    const afterHeadingTag = content.indexOf('>', content.indexOf('</h', faqHeading));
    const rest = content.substring(afterHeadingTag + 1);
    const nextH2 = rest.search(/<h2[^>]*>/i);
    const faqSection = nextH2 === -1 ? rest : rest.substring(0, nextH2);
    const h3Regex = /<h3[^>]*>([^<]+)<\/h3>/g;
    const questions: { question: string; answer: string }[] = [];
    let match;
    while ((match = h3Regex.exec(faqSection)) !== null) {
      const question = match[1].trim();
      const afterH3 = faqSection.substring(match.index + match[0].length);
      const answerMatch = afterH3.match(/<p[^>]*>([\s\S]*?)<\/p>/);
      if (answerMatch) {
        const answer = answerMatch[1].replace(/<[^>]+>/g, '').trim();
        // A question ends in a question mark. Without this, headings such as
      // "How We Evaluated These CRM Solutions" were emitted as Questions.
      if (question && answer && question.endsWith('?')) questions.push({ question, answer });
      }
    }
    if (questions.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": questions.map(q => ({
        "@type": "Question",
        "name": q.question,
        "acceptedAnswer": { "@type": "Answer", "text": q.answer }
      }))
    };
  }, [article?.content]);

  // Auto-detect HowTo articles and generate HowTo JSON-LD schema
  const howToSchema = useMemo(() => {
    if (!article?.title || !article?.content) return null;
    const title = article.title.toLowerCase();
    if (!title.includes('how to') && !title.includes('how i') && !title.includes('ways to') && !title.includes('steps to')) return null;
    const content = article.content;
    const h2Regex = /<h2[^>]*>([^<]+)<\/h2>/g;
    const steps: { name: string; text: string }[] = [];
    let match;
    while ((match = h2Regex.exec(content)) !== null) {
      const stepName = match[1].replace(/^\d+[\.\)]\s*/, '').trim();
      if (stepName.toLowerCase().includes('frequently asked') || stepName.toLowerCase().includes('faq')) continue;
      const afterH2 = content.substring(match.index + match[0].length);
      const nextH2 = afterH2.indexOf('<h2');
      const section = nextH2 > -1 ? afterH2.substring(0, nextH2) : afterH2.substring(0, 500);
      const pMatch = section.match(/<p[^>]*>([\s\S]*?)<\/p>/);
      if (pMatch) {
        const text = pMatch[1].replace(/<[^>]+>/g, '').trim();
        if (stepName && text) steps.push({ name: stepName, text: text.substring(0, 200) });
      }
    }
    if (steps.length < 2) return null;
    return {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": article.title,
      "description": article.meta_description || article.excerpt || '',
      "step": steps.map((s, i) => ({
        "@type": "HowToStep",
        "position": i + 1,
        "name": s.name,
        "text": s.text
      }))
    };
  }, [article?.title, article?.content, article?.meta_description, article?.excerpt]);

  useEffect(() => {
    if (slug) {
      // Skip API call if SSR already provided this article's data
      if (ssrData && ssrData.slug === slug) {
        // Still load related articles in background
        articleService.getRelatedArticles(slug)
          .then(related => setRelatedArticles(related.slice(0, 4)))
          .catch(err => console.error('Error loading related articles:', err));
        // Clean up SSR data to avoid stale data on client navigation
        delete (window as any).__ARTICLE_DATA__;
        return;
      }
      loadArticle(slug);
    }
  }, [slug]);

  // Count a reader view once the tab has been visible for a few seconds. The
  // server no longer counts page renders, which were mostly crawlers
  // (src/lib/articleView.ts).
  useEffect(
    () => reportViewWhenRead(slug || '', () => articleService.recordView(slug as string)),
    [slug],
  );

  // Load Twitter widget script for tweet embeds and naked tweet URLs
  useEffect(() => {
    if (!sanitizedContent || loading) return;

    const hasTweetBlockquote = sanitizedContent.includes('twitter-tweet');
    const hasTweetUrl = /https:\/\/(twitter\.com|x\.com)\/\w+\/status\/\d+/.test(sanitizedContent);
    if (!hasTweetBlockquote && !hasTweetUrl) return;

    const ensureWidgetScript = (): Promise<void> => {
      return new Promise((resolve) => {
        if ((window as any).twttr?.widgets) { resolve(); return; }
        if (!document.querySelector('script[src*="platform.twitter.com/widgets.js"]')) {
          const script = document.createElement('script');
          script.src = 'https://platform.twitter.com/widgets.js';
          script.async = true;
          script.charset = 'utf-8';
          script.onload = () => resolve();
          document.body.appendChild(script);
        } else {
          // Script exists but twttr not ready yet, poll briefly
          const check = setInterval(() => {
            if ((window as any).twttr?.widgets) { clearInterval(check); resolve(); }
          }, 100);
          setTimeout(() => { clearInterval(check); resolve(); }, 5000);
        }
      });
    };

    const processEmbeds = async () => {
      await ensureWidgetScript();
      const twttr = (window as any).twttr;
      if (!twttr?.widgets) return;

      // 1. Process standard blockquote embeds
      twttr.widgets.load();

      // 2. Convert naked tweet URLs to embeds
      const articleEl = document.querySelector('[class*="article-content"], [class*="articleContent"], .prose');
      const contentEl = articleEl || document.querySelector('[dangerouslySetInnerHTML]')?.parentElement;
      if (!contentEl) return;

      const links = contentEl.querySelectorAll('a[href]');
      links.forEach((link: Element) => {
        const href = link.getAttribute('href') || '';
        const tweetMatch = href.match(/https:\/\/(twitter\.com|x\.com)\/\w+\/status\/(\d+)/);
        if (!tweetMatch) return;

        // Skip links inside blockquotes (those are part of existing embeds)
        if (link.closest('blockquote.twitter-tweet')) return;
        // Skip links that are inside an already-rendered twitter iframe
        if (link.closest('iframe')) return;

        const tweetId = tweetMatch[2];
        const parent = link.parentElement;
        if (!parent) return;

        // Only convert if the link is the sole/primary content of its parent
        // (i.e., it's a standalone URL, not inline within a paragraph of text)
        const parentText = parent.textContent?.trim() || '';
        const linkText = link.textContent?.trim() || '';
        const isStandalone = parentText === linkText || parentText === href;

        if (!isStandalone) return;

        // Check if we already processed this
        if (parent.getAttribute('data-tweet-processed')) return;
        parent.setAttribute('data-tweet-processed', 'true');

        // Create container and embed the tweet
        const container = document.createElement('div');
        container.style.margin = '20px 0';
        parent.replaceWith(container);
        twttr.widgets.createTweet(tweetId, container, {
          align: 'center',
          conversation: 'none'
        });
      });
    };

    const timer = setTimeout(processEmbeds, 200);
    return () => clearTimeout(timer);
  }, [sanitizedContent, loading]);

  const loadArticle = async (articleSlug: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await articleService.getArticle(articleSlug);
      setArticle(data);

      try {
        const related = await articleService.getRelatedArticles(articleSlug);
        setRelatedArticles(related.slice(0, 4));
      } catch (err) {
        console.error('Error loading related articles:', err);
      }
    } catch (err) {
      setError('Article not found');
      console.error('Error loading article:', err);
    } finally {
      setLoading(false);
    }
  };

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const y = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setShowToc(false);
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (loading) {
    return <ArticlePageSkeleton />;
  }

  if (error || !article) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Article Not Found</h1>
          <p className="text-slate-500 mb-4">{error}</p>
          <Link to="/" className="text-blue-600 hover:underline">← Back to Home</Link>
        </div>
      </div>
    );
  }

  // Editor-set "Last updated" (display_updated_at) only. `updated_at` used to be the
  // fallback, but it is a row-modified timestamp, not an editorial one: a single bulk
  // import stamped 2025-09-21 onto 15,313 of 18,497 articles (82.8%), so the byline and
  // the JSON-LD dateModified both asserted a revision that never happened. Google
  // discounts unreliable dates rather than penalising them, so the cost was not ranking
  // — it was that the 278 genuine revisions became indistinguishable from import noise.
  // Do NOT backfill display_updated_at from updated_at; that inference is what caused it.
  const effectiveUpdatedAt = article.display_updated_at || '';
  const isUpdated = !!effectiveUpdatedAt && !!article.published_at &&
    new Date(effectiveUpdatedAt).getTime() - new Date(article.published_at).getTime() > 86400000;

  // The dek under the H1 is `excerpt`. For 14,578 of 18,497 articles (78.8%) it is a
  // verbatim truncation of the article's own first paragraph, so the reader is shown
  // the same sentence twice; 2,024 of those leak raw markup and render "<!--" as body
  // copy. Suppress it in exactly those three cases — a hand-written dek still shows.
  // Client-only: crawlers never saw this, so it is a "stop looking broken" fix, not SEO.
  const showExcerpt = (() => {
    const dek = (article.excerpt || '').trim();
    if (!dek) return false;
    if (dek.includes('<')) return false;                 // leaked markup
    if (/(\u2026|\.\.\.)$/.test(dek)) return false;       // machine truncation
    const normalise = (t: string) =>
      t.toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
    const body = normalise(
      (article.content || '').replace(/<[^>]*>/g, ' ').replace(/&[a-z]+;/gi, ' ').slice(0, 1200)
    );
    const head = normalise(dek).slice(0, 120);
    if (head && body.startsWith(head)) return false;     // duplicates paragraph 1
    return true;
  })();

  const metaDescription = clampDescription(article.meta_description || article.excerpt);
  const shareImage = articleShareImage(article);

  // Custom JSON-LD pasted by editors (ItemList / FAQPage / BreadcrumbList / Event etc.).
  // Validate-on-emit so a malformed paste doesn't break the script tag — drop silently if invalid.
  // Re-stringify after parse so the emitted <script> contents are a single compact line.
  // Storing the raw editor-pretty-printed string with CRLF + indentation as the children of
  // a <script> inside react-helmet-async caused the tag to silently drop during SSR — the
  // three sibling JSON.stringify-emitted schemas above survive because they are single-line.
  let customSchemaJsonld: string | null = null;
  if (article.schema_jsonld) {
    try {
      customSchemaJsonld = JSON.stringify(JSON.parse(article.schema_jsonld));
    } catch {
      customSchemaJsonld = null;
    }
  }

  const structuredData: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": article.title,
    "description": article.excerpt || article.meta_description,
    "image": article.featured_image ? [article.featured_image] : undefined,
    "datePublished": article.published_at,
    // No "url" on the Person: /author/<slug> 301s to the homepage, and asserting a
    // redirect as an author's canonical page is a bad E-E-A-T signal. Person is valid
    // without one.
    "author": {
      "@type": "Person",
      "name": article.author_name
    },
    "publisher": {
      "@type": "Organization",
      "name": "SaveDelete",
      "logo": { "@type": "ImageObject", "url": "https://savedelete.com/media/gallery/images/2025/09/logo_sd_new.png" }
    },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://savedelete.com/article/${article.slug}/` },
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", ".article-content > p:first-of-type"]
    }
  };
  if (isUpdated) {
    structuredData.dateModified = effectiveUpdatedAt;
  }

  return (
    <>
      <ReadingProgress />
      {/* Desktop sticky ToC — fixed right rail on xl+ (>=1280px) where article body has whitespace beside it */}
      {toc.length > 2 && (
        <aside className="hidden xl:block fixed top-24 right-6 w-60 max-h-[calc(100vh-8rem)] overflow-y-auto z-30">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">On this page</h3>
            <nav className="space-y-1.5">
              {toc.map((item: TocItem) => (
                <button
                  key={item.id}
                  onClick={() => scrollToHeading(item.id)}
                  className={`block w-full text-left text-sm text-slate-700 hover:text-blue-600 transition-colors leading-snug ${
                    item.level === 3 ? 'pl-3 text-xs text-slate-500' : ''
                  }`}
                >
                  {item.text}
                </button>
              ))}
            </nav>
          </div>
        </aside>
      )}
      {/* Title and descriptions match what server.js renders for this article: no
          " | SaveDelete" suffix (it pushed titles past 60 characters and, once the
          editor's meta_title carried its own suffix, doubled it), and the description
          clamped by the same function. The share image is the server's too: the
          absolute JPEG/PNG og_image, not the relative WebP featured_image. */}
      <Helmet>
        <title>{article.meta_title || article.title}</title>
        <meta name="description" content={metaDescription} />
        {['tips-on-choosing-an-escort-girl-in-paris','maximize-your-wins-with-the-aviatrix-signal-telegram-bot-real-time-alerts-and-insights','the-brand-new-of-silicone-sex-dolls','how-to-fake-a-fever'].includes(article.slug) && <meta name="robots" content="noindex, follow" />}
        <link rel="canonical" href={`https://savedelete.com/article/${article.slug}/`} />
        <meta property="og:title" content={article.meta_title || article.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://savedelete.com/article/${article.slug}/`} />
        <meta property="og:image" content={shareImage} />
        <meta property="og:site_name" content="SaveDelete" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@Save_Delete" />
        <meta name="twitter:image" content={shareImage} />
        {/* Preload LCP image for faster loading */}
        {article.featured_image && (
          <link
            rel="preload"
            as="image"
            href={article.featured_image}
            imageSrcSet={article.featured_image_srcset}
            imageSizes="(max-width: 768px) 100vw, 800px"
          />
        )}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
        {howToSchema && <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>}
        {customSchemaJsonld && <script type="application/ld+json">{customSchemaJsonld}</script>}
      </Helmet>

      {/* The main landmark and the skip-link target. The article template had
          neither, so keyboard and screen-reader users had no way past the header. */}
      <main id="main-content" className="max-w-3xl mx-auto px-4 py-8">
        {/* AdSense slot — replaces breadcrumb on all viewports (SD-MobileArticle-Apr2026) */}
        <div className="mb-6">
          <MobileArticleAd />
        </div>

        {/* Header */}
        <header className="mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight mb-4">
            {article.title}
          </h1>

          {/* Series navigator chip — shown when this article belongs to a series */}
          {article.series && article.series_order && article.series.articles.length > 0 && (
            <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full text-xs">
              <svg className="w-3.5 h-3.5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <span className="font-semibold text-purple-700">Part {article.series_order} of {article.series.articles.length}</span>
              <span className="text-purple-400">·</span>
              <span className="text-purple-700">{article.series.title}</span>
            </div>
          )}

          {showExcerpt && (
            <p className="text-lg text-slate-600 mb-4">{article.excerpt}</p>
          )}

          <div className="flex items-center gap-1.5 text-sm text-slate-500 flex-wrap">
            {/* Plain text, not a link: /author/<slug> 301s to the homepage for every
                author on the site, so this was a guaranteed dead end. Restore the link
                only if real author pages are built. */}
            <span className="font-medium text-slate-700 whitespace-nowrap">
              {article.author_name}
            </span>
            <span>·</span>
            <time dateTime={article.published_at} className="whitespace-nowrap">
              {format(new Date(article.published_at), 'MMM d, yyyy')}
            </time>
            {isUpdated && (
              <>
                <span>·</span>
                <span className="whitespace-nowrap">
                  Updated {format(new Date(effectiveUpdatedAt), 'MMM d, yyyy')}
                </span>
              </>
            )}
            <span>·</span>
            <span className="whitespace-nowrap">{readingTime} min read</span>
            {!!article.comments_count && article.comments_count > 0 && (
              <>
                <span>·</span>
                <a href="#comments" className="whitespace-nowrap hover:text-blue-600">
                  {article.comments_count} {article.comments_count === 1 ? 'comment' : 'comments'}
                </a>
              </>
            )}
            {article.category_name && (
              <>
                <span>·</span>
                <Link
                  to={`/category/${article.category}`}
                  className="text-blue-600 hover:text-blue-700 whitespace-nowrap"
                >
                  {article.category_name}
                </Link>
              </>
            )}
          </div>
        </header>

        {/* Featured Image - LCP Element */}
        {article.featured_image && (
          <figure className="mb-8 -mx-4 md:mx-0">
            <img
              src={article.featured_image}
              srcSet={article.featured_image_srcset}
              sizes={article.featured_image_sizes || '(max-width: 768px) 100vw, 800px'}
              alt={article.title}
              width={article.featured_image_width || 1200}
              height={article.featured_image_height || 675}
              className="w-full rounded-lg md:rounded-xl"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </figure>
        )}

        {/* Mobile/tablet Table of Contents — collapsible accordion (hidden on xl+ where sticky rail takes over) */}
        {toc.length > 2 && (
          <div className="xl:hidden mb-8 border border-slate-200 rounded-lg">
            <button
              onClick={() => setShowToc(!showToc)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <span>Table of Contents</span>
              <svg className={`w-4 h-4 transition-transform ${showToc ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showToc && (
              <div className="px-4 pb-4">
                <nav className="space-y-1">
                  {toc.map((item: TocItem, index: number) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToHeading(item.id)}
                      className={`flex items-start gap-3 w-full text-left text-sm text-slate-600 hover:text-blue-600 py-1.5 group ${
                        item.level === 3 ? 'pl-8' : ''
                      }`}
                    >
                      <span className="text-xs font-mono text-slate-400 group-hover:text-blue-500 mt-0.5 w-5 flex-shrink-0">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span>{item.text}</span>
                    </button>
                  ))}
                </nav>
              </div>
            )}
          </div>
        )}

        {/* Key Takeaways callout — rendered when populated, above the lead paragraph */}
        {Array.isArray(article.takeaways) && article.takeaways.length > 0 && (
          <aside className="mb-8 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-5">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-3">
              Key Takeaways
            </h2>
            <ul className="space-y-2 text-slate-700 text-base leading-relaxed">
              {article.takeaways.map((point: string, i: number) => (
                <li key={i} className="flex items-start gap-3">
                  <svg className="w-4 h-4 text-blue-500 mt-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </aside>
        )}

        {/* Article Content — split only around an embedded widget marker. The mid-article
            newsletter box was removed 2026-10-23: 2 signups in 14 days, 0 via one-click. */}
        {(() => {
          const _proseCls = "article-content prose prose-slate prose-lg max-w-none";
          const _markerRe = /<div[^>]*data-widget="([a-z0-9-]+)"[^>]*>[\s\S]*?<\/div>/i;
          const _mk = sanitizedContent.match(_markerRe);
          const _Widget = _mk ? WIDGET_REGISTRY[_mk[1]] : undefined;
          if (_mk && _Widget) {
            const _b = sanitizedContent.slice(0, _mk.index);
            const _a = sanitizedContent.slice((_mk.index ?? 0) + _mk[0].length);
            return (
              <>
                <div className={_proseCls} dangerouslySetInnerHTML={{ __html: _b }} />
                <_Widget />
                <div className={_proseCls} dangerouslySetInnerHTML={{ __html: _a }} />
              </>
            );
          }
          return <div className={_proseCls} dangerouslySetInnerHTML={{ __html: sanitizedContent }} />;
        })()}

        {/* Second AdSense slot (SD-Article-Desktop-Sep2026): end of the article body,
            where the reader has finished and before the share row. The slot at the top of
            the template is the only other one on this page. */}
        <AdUnit slot="5054246008" className="mt-8" />

        {/* Share Buttons */}
        <div className="mt-8 pt-6 border-t border-slate-200">
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">Share:</span>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Share on X"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Share on Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Share on LinkedIn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            <button
              onClick={() => navigator.clipboard.writeText(shareUrl)}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Copy link"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <Link
                key={tag.id}
                to={`/tag/${tag.slug}`}
                className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm hover:bg-slate-200 transition-colors"
              >
                {tag.name}
              </Link>
            ))}
          </div>
        )}

      </main>

      {/* Series box — full list of series members, between article body and Related Articles */}
      {article.series && article.series.articles.length > 0 && (
        <section className="border-t border-slate-200 bg-purple-50/40">
          <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-purple-600">Series</span>
            </div>
            <h2 className="text-lg font-bold text-slate-900 mb-2">{article.series.title}</h2>
            {article.series.description && (
              <p className="text-sm text-slate-600 mb-4">{article.series.description}</p>
            )}
            <ol className="space-y-2">
              {article.series.articles.map((member) => {
                const isCurrent = member.slug === article.slug;
                return (
                  <li key={member.id} className="flex items-baseline gap-3">
                    <span className="text-xs font-mono text-slate-400 w-7 flex-shrink-0">
                      {String(member.series_order || 0).padStart(2, '0')}
                    </span>
                    {isCurrent ? (
                      <span className="text-sm font-medium text-slate-900">
                        {member.title}
                        <span className="ml-2 text-xs text-purple-600">(you are here)</span>
                      </span>
                    ) : (
                      <Link to={`/article/${member.slug}/`} className="text-sm text-blue-600 hover:underline">
                        {member.title}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        </section>
      )}

      {/* Previous / next in the category (blog/adjacent.py, 2026-10-07). The same
          links server.js renders, so each editorial article keeps its two inbound
          chain links in the rendered page as well as in the raw HTML. Link posts
          live outside the app (/read/...), so they get a plain <a>. */}
      {(article.adjacent_articles?.previous || article.adjacent_articles?.next) && (
        <section className="border-t border-slate-200 bg-white">
          <nav
            aria-label={`More in ${article.category_name || 'this category'}`}
            className="max-w-3xl mx-auto px-4 py-6 grid gap-4 sm:grid-cols-2"
          >
            {(['previous', 'next'] as const).map((side) => {
              const link = article.adjacent_articles?.[side];
              if (!link) return <span key={side} aria-hidden="true" />;
              const className = `group block rounded-lg border border-slate-200 p-4 hover:border-blue-300${side === 'next' ? ' sm:text-right' : ''}`;
              const inner = (
                <>
                  <span className="block text-xs font-medium uppercase tracking-wide text-slate-500">
                    {side === 'previous' ? 'Previous article' : 'Next article'}
                  </span>
                  <span className="mt-1 block text-sm font-semibold text-slate-900 group-hover:text-blue-600">
                    {link.title}
                  </span>
                </>
              );
              return link.url.startsWith('/article/')
                ? <Link key={side} to={link.url} className={className}>{inner}</Link>
                : <a key={side} href={link.url} className={className}>{inner}</a>;
            })}
          </nav>
        </section>
      )}

      {/* Related Articles — between article body and author bio */}
      {relatedArticles.length > 0 && (
        <section className="border-t border-slate-200 bg-white py-10">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedArticles.map((related) => (
                /* headingLevel 3: these sit under the "Related Articles" h2, so
                   rendering them as h2 made them its siblings in the outline. */
                <ArticleCard key={related.id} article={related} headingLevel={3} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Author bio — under Related Articles */}
      {article.author_bio && (
        <section className="border-t border-slate-200 bg-slate-50">
          <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="flex items-start gap-4">
              {article.author_avatar ? (
                <img
                  src={article.author_avatar}
                  alt={article.author_name}
                  className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                  loading="lazy"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-slate-300 flex items-center justify-center text-white text-xl font-semibold flex-shrink-0">
                  {article.author_name.charAt(0)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline flex-wrap gap-x-2 mb-1">
                  <h3 className="text-base font-semibold text-slate-900">{article.author_name}</h3>
                  {!!article.author_article_count && (
                    <span className="text-xs text-slate-500">
                      · {article.author_article_count} {article.author_article_count === 1 ? 'article' : 'articles'}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">{article.author_bio}</p>
                <div className="flex items-center gap-3 text-sm">
                  {article.author_twitter && (
                    <a
                      href={`https://twitter.com/${article.author_twitter.replace(/^@/, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-500 hover:text-slate-700"
                      aria-label={`${article.author_name} on X`}
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </a>
                  )}
                  {article.author_linkedin && (
                    <a
                      href={article.author_linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-500 hover:text-slate-700"
                      aria-label={`${article.author_name} on LinkedIn`}
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Related Product Guides */}
      {article && (
        <RelatedGuides
          articleTitle={article.title || ''}
          categoryName={article.category_name || ''}
          tags={article.tags ? article.tags.map((t: any) => typeof t === 'string' ? t : t.name || '') : []}
        />
      )}

      {/* Comments — lazy-loaded below the fold via IntersectionObserver */}
      <div id="comments">
        <Comments articleSlug={article.slug} allowComments={article.allow_comments !== false} />
      </div>
    </>
  );
};

export default EnhancedArticlePage;
